import React from 'react';

/**
 * UpgradeSelect — Level-up choice overlay
 *
 * Pauses the game and shows 3 random upgrade options.
 * Player picks one, game resumes.
 */

const rarityColors = {
  common: '#00ff41',
  uncommon: '#00d4ff',
  rare: '#a855f7',
  legendary: '#ffcc00',
};

const rarityGlow = {
  common: 'rgba(0, 255, 65, 0.3)',
  uncommon: 'rgba(0, 212, 255, 0.3)',
  rare: 'rgba(168, 85, 247, 0.3)',
  legendary: 'rgba(255, 204, 0, 0.4)',
};

const styles = {
  overlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(6, 6, 12, 0.85)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    fontFamily: "'Orbitron', sans-serif",
    animation: 'fadeIn 0.3s ease-out',
  },
  title: {
    fontSize: '24px',
    fontWeight: 900,
    color: '#a855f7',
    marginBottom: '8px',
    textShadow: '0 0 20px rgba(168, 85, 247, 0.6)',
    letterSpacing: '3px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#888',
    marginBottom: '30px',
    letterSpacing: '2px',
  },
  cards: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    padding: '0 20px',
    maxWidth: '600px',
  },
  card: {
    width: '160px',
    padding: '20px 16px',
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    pointerEvents: 'auto',
  },
  cardName: {
    fontSize: '14px',
    fontWeight: 700,
    marginBottom: '8px',
    letterSpacing: '1px',
  },
  cardDesc: {
    fontSize: '11px',
    color: '#ccc',
    fontFamily: "'JetBrains Mono', monospace",
    lineHeight: '1.4',
  },
  cardRarity: {
    fontSize: '9px',
    marginTop: '10px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
};

export default function UpgradeSelect({ upgrades, onSelect }) {
  if (!upgrades || upgrades.length === 0) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.title}>LEVEL UP</div>
      <div style={styles.subtitle}>CHOOSE YOUR UPGRADE</div>
      <div style={styles.cards}>
        {upgrades.map((u, i) => {
          const color = rarityColors[u.rarity] || '#00ff41';
          const glow = rarityGlow[u.rarity] || 'rgba(0, 255, 65, 0.3)';
          return (
            <div
              key={u.id}
              style={{
                ...styles.card,
                border: `1px solid ${color}`,
                boxShadow: `0 0 15px ${glow}, inset 0 0 20px rgba(0,0,0,0.5)`,
                background: `linear-gradient(135deg, rgba(6,6,12,0.9) 0%, rgba(20,20,32,0.9) 100%)`,
              }}
              onClick={() => onSelect(u)}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'scale(1.08)';
                e.currentTarget.style.boxShadow = `0 0 25px ${glow}, 0 0 40px ${glow}`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = `0 0 15px ${glow}, inset 0 0 20px rgba(0,0,0,0.5)`;
              }}
            >
              <div style={{ ...styles.cardName, color }}>{u.name}</div>
              <div style={styles.cardDesc}>{u.desc || ''}</div>
              <div style={{ ...styles.cardRarity, color }}>{u.rarity}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
